import Decimal from "decimal.js";

/**
 * Generic aggregation utilities for data processing
 */

/**
 * Aggregate values by a key
 * @param data Array of items to aggregate
 * @param keyExtractor Function to extract the aggregation key from each item
 * @param valueExtractor Function to extract the numeric value from each item
 * @returns Map of keys to aggregated sums
 */
export function aggregateByKey<T>(
  data: T[],
  keyExtractor: (item: T) => string,
  valueExtractor: (item: T) => number,
): Map<string, number> {
  const aggregationMap = new Map<string, number>();

  for (const item of data) {
    const key = keyExtractor(item);
    const value = valueExtractor(item);
    aggregationMap.set(key, (aggregationMap.get(key) || 0) + value);
  }

  return aggregationMap;
}

/**
 * Group items by a key
 * @param data Array of items to group
 * @param keyExtractor Function to extract the grouping key from each item
 * @returns Map of keys to arrays of items
 */
export function groupBy<T>(data: T[], keyExtractor: (item: T) => string): Map<string, T[]> {
  const groups = new Map<string, T[]>();

  for (const item of data) {
    const key = keyExtractor(item);
    const group = groups.get(key) || [];
    group.push(item);
    groups.set(key, group);
  }

  return groups;
}

/**
 * Aggregate values by multiple keys
 * @param data Array of items to aggregate
 * @param keyExtractors Array of functions to extract keys from each item
 * @param valueExtractor Function to extract the numeric value from each item
 * @returns Map of composite keys to aggregated sums
 */
export function aggregateByMultipleKeys<T>(
  data: T[],
  keyExtractors: ((item: T) => string | null)[],
  valueExtractor: (item: T) => Decimal,
): Map<string, Decimal> {
  const aggregationMap = new Map<string, Decimal>();

  for (const item of data) {
    const keyParts = keyExtractors.map((extractor) => extractor(item) || "null");
    const key = JSON.stringify(keyParts);
    const value = valueExtractor(item);
    aggregationMap.set(key, (aggregationMap.get(key) || new Decimal(0)).plus(value));
  }

  return aggregationMap;
}

/**
 * Calculate time bucket for a date
 * @param date The date to bucket
 * @param interval The bucketing interval
 * @returns The bucketed date
 */
export function timeBucket(date: Date, interval: "minute" | "hour" | "day" | "week" | "month" | "year"): Date {
  const result = new Date(date);

  switch (interval) {
    case "minute":
      result.setSeconds(0, 0);
      break;
    case "hour":
      result.setMinutes(0, 0, 0);
      break;
    case "day":
      result.setHours(0, 0, 0, 0);
      break;
    case "week": {
      result.setHours(0, 0, 0, 0);
      const day = result.getDay();
      const diff = result.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
      result.setDate(diff);
      break;
    }
    case "month":
      result.setDate(1);
      result.setHours(0, 0, 0, 0);
      break;
    case "year":
      result.setMonth(0, 1);
      result.setHours(0, 0, 0, 0);
      break;
  }

  return result;
}

/**
 * Aggregate time series data into buckets
 * @param data Array of items with timestamps
 * @param timestampExtractor Function to extract timestamp from each item
 * @param valueExtractor Function to extract numeric value from each item
 * @param interval Time bucket interval
 * @returns Map of bucketed timestamps to aggregated values
 */
export function aggregateTimeSeries<T>(
  data: T[],
  timestampExtractor: (item: T) => Date,
  valueExtractor: (item: T) => number,
  interval: "minute" | "hour" | "day" | "week" | "month" | "year",
): Map<string, number> {
  const aggregationMap = new Map<string, number>();

  for (const item of data) {
    const timestamp = timestampExtractor(item);
    const bucket = timeBucket(timestamp, interval);
    const key = bucket.toISOString();
    const value = valueExtractor(item);
    aggregationMap.set(key, (aggregationMap.get(key) || 0) + value);
  }

  return aggregationMap;
}

/**
 * Calculate rolling aggregation over a window
 * @param data Array of numeric values
 * @param windowSize Size of the rolling window
 * @param aggregationFn Function to aggregate values in the window (default: sum)
 * @returns Array of aggregated values
 */
export function rollingAggregate(
  data: number[],
  windowSize: number,
  aggregationFn: (window: number[]) => number = (w) => w.reduce((a, b) => a + b, 0),
): number[] {
  if (windowSize <= 0 || windowSize > data.length) {
    throw new Error("Invalid window size");
  }

  const result: number[] = [];

  for (let i = 0; i <= data.length - windowSize; i++) {
    const window = data.slice(i, i + windowSize);
    result.push(aggregationFn(window));
  }

  return result;
}

/**
 * Pivot data from rows to columns
 * @param data Array of items to pivot
 * @param rowKeyExtractor Function to extract row key
 * @param columnKeyExtractor Function to extract column key
 * @param valueExtractor Function to extract value
 * @returns Map of row keys to Maps of column keys to values
 */
export function pivot<T>(
  data: T[],
  rowKeyExtractor: (item: T) => string,
  columnKeyExtractor: (item: T) => string,
  valueExtractor: (item: T) => number,
  aggregationFn: (values: number[]) => number = (values) => values.reduce((a, b) => a + b, 0),
): Map<string, Map<string, number>> {
  const result = new Map<string, Map<string, number>>();
  const tempData = new Map<string, Map<string, number[]>>();

  // Collect all values
  for (const item of data) {
    const rowKey = rowKeyExtractor(item);
    const colKey = columnKeyExtractor(item);
    const value = valueExtractor(item);

    if (!tempData.has(rowKey)) {
      tempData.set(rowKey, new Map());
    }
    const row = tempData.get(rowKey)!;

    if (!row.has(colKey)) {
      row.set(colKey, []);
    }
    row.get(colKey)!.push(value);
  }

  // Aggregate values
  for (const [rowKey, row] of Array.from(tempData.entries())) {
    const aggregatedRow = new Map<string, number>();
    for (const [colKey, values] of Array.from(row.entries())) {
      aggregatedRow.set(colKey, aggregationFn(values));
    }
    result.set(rowKey, aggregatedRow);
  }

  return result;
}
